<template>
	<footer v-show="showAppFooter" class="jw-app-footer container-fluid container-max-width">
		<div class="jw-app-footer__heading">
			<div class="jw-app-footer__heading-left">
				<div>
					<strong class="jw-app-footer__heading-left-brand">JustWatch </strong>
					<span class="jw-app-footer__heading-left-separator">|</span> {{ $t('WEBAPP_FOOTER_TAGLINE') }}
				</div>

				<a
					href="https://www.justwatch.com/us/talent?utm_source=jw-app-footer"
					class="basic-button primary rounded jw-app-footer__heading-left-hiring"
				>
					We are hiring!
				</a>
			</div>

			<div>
				<div>
					&copy; {{ currentYear }} JustWatch {{ $t('WEBAPP_COPYRIGHT_DISCLAIMER') }}
					<span style="color: var(--jw-color-secondary)">({{ version }})</span>
				</div>
				<ConsentToggleButton />
			</div>
		</div>

		<hr />

		<div class="footer-links" v-if="titleUrlsEnabled">
			<!-- top movies -->
			<div>
				<h2>{{ $t('WEBAPP_FOOTER_TOP_X_MOVIES', { x: topMovies.length }) }}</h2>
				<ul class="list-unstyled">
					<li v-for="(title, index) in topMovies" :key="title.id" class="jw-app-footer__link">
						<SmartLink
							@click.native="trackClick(title, $t('WEBAPP_FOOTER_TOP_X_MOVIES', { x: 5 }), index)"
							:to="{ path: title.content.fullPath }"
						>
							{{ title.content.title }}
						</SmartLink>
					</li>
				</ul>
			</div>

			<!-- top shows -->
			<div>
				<h2>{{ $t('WEBAPP_FOOTER_TOP_X_SHOWS', { x: topShows.length }) }}</h2>
				<ul class="list-unstyled">
					<li v-for="(title, index) in topShows" :key="title.id" class="jw-app-footer__link">
						<SmartLink
							@click.native="trackClick(title, $t('WEBAPP_FOOTER_TOP_X_SHOWS', { x: 5 }), index)"
							:to="{ path: title.content.fullPath }"
						>
							{{ title.content.title }}
						</SmartLink>
					</li>
				</ul>
			</div>

			<!-- top providers -->
			<div>
				<h2>{{ $t('WEBAPP_FOOTER_TOP_X_PROVIDERS', { x: providersLimit }) }}</h2>
				<ul class="list-unstyled">
					<li
						v-for="(provider, index) in providers().slice(0, providersLimit)"
						:key="provider.packageId"
						class="jw-app-footer__link"
					>
						<SmartLink
							@click.native="handleTopProvider(provider, CollectionType.POPULAR, index)"
							:to="{
								name: 'app.titles.popular.provider',
								params: {
									provider: provider.slug,
								},
							}"
						>
							{{ provider.clearName }}
						</SmartLink>
					</li>
				</ul>
			</div>

			<!-- top providers new titles -->
			<div>
				<h2>{{ $t('WEBAPP_FOOTER_TOP_X_NEW_ON_PROVIDER', { x: providersLimit }) }}</h2>
				<ul class="list-unstyled">
					<li
						v-for="(provider, index) in providers().slice(0, providersLimit)"
						:key="provider.packageId"
						class="jw-app-footer__link"
					>
						<SmartLink
							@click.native="handleTopProvider(provider, CollectionType.NEW, index)"
							:to="{
								name: 'app.titles.new.provider',
								params: {
									provider: provider.slug,
								},
							}"
						>
							{{
								$t('WEBAPP_FOOTER_NEW_ON_PROVIDER', {
									provider: provider.clearName,
								})
							}}
						</SmartLink>
					</li>
				</ul>
			</div>

			<!-- next upcoming movies -->
			<div v-if="topUpcomingMovies.length > 0">
				<h2>
					<SmartLink v-if="shouldLinkToUpcoming" :to="{ name: 'app.titles.upcoming.movie' }">
						{{ $t('WEBAPP_UPCOMING_MOVIES_FOOTER') }}
					</SmartLink>
					<span v-else>{{ $t('WEBAPP_UPCOMING_MOVIES_FOOTER') }}</span>
				</h2>
				<ul class="list-unstyled">
					<li v-for="(title, index) in topUpcomingMovies" :key="title.id" class="jw-app-footer__link">
						<SmartLink
							@click.native="trackClick(title, 'Next upcoming movies', index)"
							:to="{ path: title.content.fullPath }"
						>
							{{ title.content.title }}
						</SmartLink>
					</li>
				</ul>
			</div>

			<!-- next upcoming shows -->
			<div v-if="topUpcomingShows.length > 0">
				<h2>
					<SmartLink v-if="shouldLinkToUpcoming" :to="{ name: 'app.titles.upcoming.show' }">
						{{ $t('WEBAPP_UPCOMING_SHOWS_FOOTER') }}
					</SmartLink>
					<span v-else>{{ $t('WEBAPP_UPCOMING_SHOWS_FOOTER') }}</span>
				</h2>
				<ul class="list-unstyled">
					<li v-for="(title, index) in topUpcomingShows" :key="title.id" class="jw-app-footer__link">
						<SmartLink
							@click.native="trackClick(title, 'Next upcoming shows', index)"
							:to="{ path: title.content.fullPath }"
						>
							{{ getShowTitle(title) }}
						</SmartLink>
					</li>
				</ul>
			</div>

			<!-- latest news articles -->
			<div v-if="topArticles.length > 0">
				<h2>
					<SmartLink
						:to="{
							name: 'app.editorial.subdomain.list',
						}"
						@click.native="trackHeadingClick(latestEditorialHeader)"
					>
						{{ latestEditorialHeader }}
					</SmartLink>
				</h2>

				<ul class="list-unstyled top-articles">
					<li v-for="(article, index) in topArticles" :key="article.id" class="jw-app-footer__link">
						<SmartLink
							:to="{
								name: 'app.editorial.subdomain.home',
							}"
							@click.native="trackClick(article, article.content.title, index)"
						>
							{{ article.content.title }}
						</SmartLink>
					</li>
				</ul>
			</div>
		</div>
	</footer>
</template>

<script lang="ts" setup>
import type { TranslateResult } from 'vue-i18n';
import type Provider from '@/interfaces/provider';
import type { TopArticleFragment } from '@/pages/graphql/queries/GetTopArticles.query';
import type { TopTitleFragment, TopTitle_Season_Fragment } from '@/pages/graphql/queries/GetTopTitles.query';

import ConsentToggleButton from '@/components/consent/ConsentToggleButton.vue';

import { COUNTRY_WITHOUT_TITLEDETAIL } from '@/constants/web-locales.constant';
import { CollectionType } from '@/enums/collection-type';

import { SnowplowTitleContext } from '@/helpers/tracking/providers';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import { storeToRefs } from 'pinia';
import { useFooterStore } from '@/stores/pinia/useFooterStore';
import { useFilterStore } from '@/stores/pinia/useFilterStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useConstantStore } from '@/stores/pinia/useConstantStore';

import { computed, ref } from 'vue';

const routesWithTitleList = [
	CollectionType.NEW,
	CollectionType.POPULAR,
	CollectionType.SEARCH,
	CollectionType.MY_LISTS,
	CollectionType.PUBLIC_LISTS,
	CollectionType.TV_SHOW_TRACKING,
];

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const { providers } = useConstantStore();
const { setFilterValue } = useFilterStore();
const languageStore = useLanguageStore();
const { topMovies, topShows, topUpcomingMovies, topUpcomingShows, topArticles } = storeToRefs(useFooterStore());

const providersLimit = ref(5);
const currentYear = new Date().getFullYear();

const version = BUILD_CONFIG.appVersion;

const showAppFooter = computed(() => {
	const currentCollection: CollectionType = route.meta?.collectionType;
	return !routesWithTitleList.includes(currentCollection);
});

const latestEditorialHeader = computed(() => t('WEBAPP_FOOTER_TOP_X_NEWS', { x: topArticles.value.length }));

const shouldLinkToUpcoming = computed(() => router.getRoutes().some(({ name }) => name === 'app.titles.upcoming.list'));

const titleUrlsEnabled = computed(() => !COUNTRY_WITHOUT_TITLEDETAIL.includes(languageStore.webLocale));

function getShowTitle(title: TopTitleFragment) {
	return [
		title.objectType === 'SHOW_SEASON' ? (title as TopTitle_Season_Fragment).show.content.title : '',
		title.content.title,
	].join(' ');
}

function handleTopProvider(provider: Provider, collectionType: CollectionType, index: number) {
	const translatable =
		collectionType === CollectionType.POPULAR
			? t('WEBAPP_FOOTER_TOP_X_PROVIDERS', { x: providersLimit.value })
			: t('WEBAPP_FOOTER_TOP_X_NEW_ON_PROVIDER', { x: providersLimit.value });

	trackProviderClick(provider, translatable, index);
	setProvider(provider, collectionType);
}

function trackHeadingClick(headline: TranslateResult) {
	TrackingHelper.trackEvent('userinteraction', {
		action: 'tabchange',
		label: `${headline}`,
		property: 'footer',
		useLastRoute: true,
	});
}

function trackProviderClick(provider: Provider, headline: TranslateResult, value: number) {
	TrackingHelper.trackEvent('userinteraction', {
		action: 'tabchange',
		label: `${headline},${provider.packageId}`,
		value,
		property: 'footer',
		useLastRoute: true,
	});
}

function trackClick(item: TopTitleFragment | TopArticleFragment, headline: TranslateResult, value: number) {
	const contexts = [];

	if (item.__typename !== 'Article') {
		contexts.push(new SnowplowTitleContext(item.objectId, item.objectType));
	}

	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'title_clicked',
			label: `${headline}`,
			value,
			property: 'footer',
			useLastRoute: true,
		},
		contexts
	);
}

function setProvider(provider: Provider, collectionType: CollectionType) {
	setFilterValue({
		filterName: 'providers',
		filterValue: [provider.shortName],
		collectionType,
		// prevents duplicate navigation
		updateRouteState: false,
	});
}
</script>

<style lang="scss" scoped>
@import '~@/styles/variables.scss';
@import '@/styles/mixins.scss';

.jw-app-footer {
	padding-top: 6em;
	margin-bottom: 1em;

	@media (max-width: $breakpoint-two-rows) {
		padding-left: 25px !important;
		padding-right: 25px !important;
	}

	h2 {
		font-size: 100%;

		a {
			color: $jw-color-lightgray;
		}
	}

	&__link {
		margin-bottom: 0.5em;
	}

	:deep(.jw-app-footer__link) {
		font-weight: normal;
	}

	.top-articles {
		li {
			@include multi-line-clamp(3);
		}
	}

	&__heading {
		width: 100%;
		display: flex;
		gap: 20px;
		justify-content: space-between;
		align-items: baseline;

		&-left {
			font-size: 18px;
			display: flex;
			gap: 10px;
			align-items: center;
			flex-wrap: wrap;

			&-brand {
				color: $jw-color-white;
			}

			&-separator {
				color: $jw-color-darkgray--light;
			}

			&-hiring {
				background: $jw-color-gold--button-gradient;
				font-size: 15px;
				padding: 10px;
				display: inline-block;
			}

			@media (max-width: $screen-sm) {
				border-inline-end: 1px solid $jw-color-darkgray--light;
				padding-inline-end: 20px;

				&-separator {
					display: none;
				}
			}
		}
	}

	.footer-links {
		display: grid;
		gap: 16px;
		grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
	}
}
</style>
